import React, {useEffect, useState} from "react";
import {ReactNode} from "react";
import {Coordinates} from "../../index";
import {Button, ButtonTypes} from "../../../react/components/ui/spectre/buttons";
import {useTranslationContext} from "../../../react/providers/TranslationContext";
import Tribe from "../../enum/Tribe";

type Props = {

}

type VillageConfig = {
    x: number,
    y: number,
    scouts: number,
}

type VillageData = {
    owner: number,
    x: number,
    y: number,
    ew: number,
    capital: boolean,
    name: string,
    tribe: Tribe
}

type PlayerData = {
    sid: number,
    name: string,
}

function readVillage(village: Record<keyof VillageData, string>): VillageData {
    return {
        x: Number(village.x),
        y: Number(village.y),
        ew: Number(village.ew),
        capital: Number(village.capital) === 1,
        name: village.name,
        tribe: Number(village.tribe) as Tribe,
        owner: Number(village.owner),
    }
}

function readPlayer(player: Record<keyof PlayerData, string>): PlayerData {
    return {
        sid: Number(player.sid),
        name: player.name,
    }
}

function parseLine(line: string): null|Array<Coordinates> {
    // Ideally it's a link ...
    if (line.includes('https')) {
        const queryString = line.substring(line.lastIndexOf('?'));
        const queryParams = new URLSearchParams(queryString);
        const x = parseInt(queryParams.get('x') ?? '');
        const y = parseInt(queryParams.get('y') ?? '');
        if (!isNaN(x) && !isNaN(y)) {
            return [{ x, y }];
        }
        return null;
    }

    // ... but if not we just try to find two numbers

    // Remove space between negative sign and number, and also replace mathematical minus sign with simple dash
    const normalizedLine = line.replace(/[−\-] ?([0-9]+)/g, '-$1');

    const matches = [...line.matchAll(/-?[0-9]+/g)];
    if (matches.length < 2) {
        return null;
    }

    // Take the _last_ two hits, because Travian shows this ingame as "[village name] (x|y)" and people might C&P,
    // so this should work even when village name contains numbers.
    const matchY = matches.pop();
    const matchX = matches.pop();
    const [x, y] = [matchX, matchY].map(match => parseInt(match[0]));
    if (!isNaN(x) && !isNaN(y)) {
        return [{ x, y }];
    }
    return null;
}

function makeLink(village: VillageConfig, tid: number): string {
    return `https://nys.x5.international.travian.com/build.php?id=39&gid=16&tt=2&x=${village.x}&y=${village.y}&eventType=4&troop[t${tid}]=${village.scouts}`;
}

export default function ScoutHelper(props: Props): ReactNode {

    const [villages, setVillages] = useState<Array<VillageConfig>>([]);
    const [showPopupBlockerInfo, setShowPopupBlockerInfo] = useState<boolean>(false);
    const [scoutTroopId, setScoutTroopId] = useState<number>(4);

    const [villageData, setVillageData] = useState<Array<VillageData>>([]);
    const [playerData, setPlayerData] = useState<Array<PlayerData>>([]);

    const {getTranslator, translate: rootTranslate} = useTranslationContext();

    const translate = getTranslator('tool/trav/ScoutHelper');

    async function loadIngameData(): Promise<void> {
        // I REALLY need to put this into proper TS stuff some time
        await Promise.all([
            // @ts-ignore
            TData.preloadPromise("d"),
            // @ts-ignore
            TData.preloadPromise("s")
        ]);
        // @ts-ignore
        const villageData = TData.getAll('d');
        // @ts-ignore
        const playerData = TData.getAll('s');
        setVillageData(villageData.map(readVillage));
        setPlayerData(playerData.map(readPlayer));
    }

    useEffect(() => {
        void loadIngameData();
    }, [])

    function onChange(newValue: string): void {
        const villages = newValue.split('\n').map(parseLine).flat(1).filter(data => data !== null);
        const configs: Array<VillageConfig> = villages.map(({x, y}) => ({x, y, scouts: 250}));
        setVillages(configs);
    }

    function openAll(): void {
        setShowPopupBlockerInfo(true);
        villages.forEach(village => window.open(makeLink(village, scoutTroopId)));
    }

    function setScoutsForVillage(index: number, value: string): void {
        const scouts = parseInt(value);
        if (isNaN(scouts)) {
            return;
        }
        const configs = [...villages];
        configs[index] = {...configs[index], scouts};
        setVillages(configs);
    }

    const enhancedVillages: Array<VillageConfig & VillageData & { playerName: string }> = villages.map(village => {
        const data = villageData.find(data => data.x === village.x && data.y === village.y);
        if (!data) {
            return {owner: 0, ew: 0, capital: false, name: '???', tribe: Tribe.Natars, playerName: '???', ...village};
        }
        const playerName = playerData.find(player => player.sid === data.owner)?.name ?? '???';
        return {playerName, ...data, ...village};
    })

    return <div>
        <textarea onChange={e => onChange(e.target.value)}></textarea>

        <div>
            <Button buttonTypes={[ButtonTypes.primary]} onClick={openAll}>{translate('openAll')}</Button>
            {showPopupBlockerInfo && <div>{translate('disablePopupBlocker')}</div>}
        </div>

        <div>
            <select value={scoutTroopId} onChange={e => setScoutTroopId(parseInt(e.target.value))}>
                <option value={2}>{rootTranslate('travian/tribe8')}</option>
                <option value={3}>{rootTranslate('travian/tribe3')} / {rootTranslate('travian/tribe6')}</option>
                <option value={4}>{rootTranslate('travian/tribe1')} / {rootTranslate('travian/tribe2')} / {rootTranslate('travian/tribe7')} / {rootTranslate('travian/tribe9')}</option>
            </select>
        </div>

        <table>
            <thead>
            <tr>
                <td colSpan={4}>{translate('village')}</td>
                <td>{translate('tribe')}</td>
                <td>{translate('population')}</td>
                <td>{translate('player')}</td>
                <td>{translate('scouts')}</td>
            </tr>
            </thead>
            <tbody>
            {enhancedVillages.map((village, index) => <tr key={index}>
                <td align='right'>{village.x}</td>
                <td>|</td>
                <td align='left'>{village.y}</td>
                <td>{village.name}</td>
                <td>{rootTranslate('travian/tribe' + village.tribe)}</td>
                <td>{village.ew}</td>
                <td>{village.playerName}</td>
                <td>
                    <input onChange={e => setScoutsForVillage(index, e.target.value)} value={village.scouts} />
                </td>
                <td><a href={makeLink(village, scoutTroopId)} target='_blank'>{translate('send')}</a></td>
            </tr>)}
            </tbody>
        </table>
    </div>
}
